/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { slotsQueryObjectHelper } from "../../../utils/slotsQueryObjectHelper";
import { getTSlots } from "../../../store/models/slots/slot.thunk";
import { IStoreSlot } from "../../../store/models/slots/typing/slot.interface";
import { useInView } from "react-intersection-observer";
import { useTranslation } from "react-i18next";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../store/hooks/store.hooks";
import { useMediaQuery } from "react-responsive";
import { Box } from "@mui/material";
import { SlotCard } from "../SlotCard/SlotCard";
import { LobbySlotsSkeletonSection } from "../LobbySlotsSkeleton/LobbySlotsSkeleton";
import "./LobbySlotsSection.css";
import {
  LobbySlotsProviderSelector,
  LobbySlotsSelector,
} from "./LobbySlots.selector";
import { slotsAttributeHelper } from "../../../utils/slotsAttributeHelper";
import { scrollTop } from "../../../utils/scrollTop";

interface ILobbySlotsSectionProps {
  action:
    | "popular"
    | "popular_live"
    | "popular_crash"
    | "recommended"
    | "bonus_buy"
    | "new"
    | "slots"
    | "live"
    | "crash_games"
    | "roulette"
    | "card_games"
    | "jackpots"
    | "mega_ways"
    | "animals"
    | "fruits"
    | "seven"
    | "search"
    | "books";
  src: string;
  name?: string;
  provider?: number;
}
export const LobbySlotsSection = ({
  action,
  src,
  name,
  provider,
}: ILobbySlotsSectionProps) => {
  const twoItems = useMediaQuery({ maxWidth: 374.98 });
  const threeItems = useMediaQuery({ minWidth: 374.98, maxWidth: 599.98 });
  const fourItems = useMediaQuery({ minWidth: 599.98, maxWidth: 1029.98 });
  const sixItems = useMediaQuery({ minWidth: 1029.98, maxWidth: 1699.98 });
  const eightItems = useMediaQuery({ minWidth: 1699.98 });
  const perPage = twoItems
    ? 50
    : threeItems
    ? 51
    : fourItems
    ? 52
    : sixItems
    ? 54
    : eightItems
    ? 56
    : 50;
  const [page, setPage] = useState<number>(1);
  const [object, setObject] = useState<{
    [key: string]: string | number | boolean | undefined;
    page: number;
    provider: number | undefined;
  }>({ ...slotsQueryObjectHelper(action, page, provider, name), perPage });
  const slotsData = useAppSelector(
    LobbySlotsSelector((() => slotsAttributeHelper(object))())
  );
  const favorites = useAppSelector(({ slots }) => slots.favouriteGames);
  const [games, setGames] = useState<IStoreSlot[]>([]);
  const gamesCount: number = slotsData ? slotsData.count : 0;
  const [loading, setLoading] = useState<boolean>(false);
  const { ref, inView } = useInView();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  async function fetchSlots() {
    setLoading(true);
    const slotsResponse = await dispatch(
      getTSlots({
        ...object,
        attribute: slotsAttributeHelper(object),
      })
    );
    if (slotsResponse.type === "gettSlots/fulfilled") {
      setLoading(false);
    }
  }
  async function searchSlots(object: {
    [key: string]: string | number | boolean | undefined;
    page: number;
    provider: number | undefined;
  }) {
    if (name) {
      if (object.page === 1) {
        setObject(object);
        setPage(object.page);
      }
      setLoading(true);
      const slotsResponse = await dispatch(
        getTSlots({
          ...object,
          attribute: slotsAttributeHelper(object),
        })
      );
      if (slotsResponse.type === "gettSlots/fulfilled") {
        setLoading(false);
        setGames((prev) => {
          const uniqueGames =
            page === 1
              ? (
                  slotsResponse.payload as {
                    games: IStoreSlot[];
                    count: Number;
                  }
                ).games
              : [
                  ...prev,
                  ...(
                    slotsResponse.payload as {
                      games: IStoreSlot[];
                      count: Number;
                    }
                  ).games,
                ];
          return uniqueGames.filter(
            (game, index, self) =>
              self.findIndex((g) => g.id === game.id) === index
          );
        });
      }
    }
  }
  useEffect(() => {
    setObject({
      ...slotsQueryObjectHelper(action, page, provider, name),
      perPage,
    });
  }, [page]);
  useEffect(() => {
    if (slotsData && slotsData.games.length > 0 && !name) {
      setGames((prev) => {
        const uniqueGames =
          page === 1 ? slotsData.games : [...prev, ...slotsData.games];
        return uniqueGames.filter(
          (game, index, self) =>
            self.findIndex((g) => g.id === game.id) === index
        );
      });
    } else if (!loading && !name) {
      fetchSlots();
    } else if (name) {
      searchSlots(object);
    }
  }, [slotsAttributeHelper(object), slotsData.games.length]);

  useEffect(() => {
    searchSlots({
      ...slotsQueryObjectHelper(action, 1, provider, name),
      perPage,
    });
  }, [name]);
  useEffect(() => {
    if (inView && !loading && Math.ceil(gamesCount / perPage) > page) {
      setPage((prev) => prev + 1);
    }
  }, [inView]);
  useEffect(() => {
    scrollTop();
  }, []);
  return (
    <Box className="lobby-slots-section">
      <Box className="lobby-slots-section__heading">
        <img src={src} alt={action} />
        {t(`main.menuItems.${action}`)}
      </Box>
      <Box
        className={`lobby-slots-section__container ${
          twoItems ? "two-items" : ""
        }${threeItems ? "three-items" : ""}${fourItems ? "four-items" : ""}${
          sixItems ? "six-items" : ""
        }${eightItems ? "eight-items" : ""}`}
      >
        {games.map((game, index) => {
          if (index + 1 === games.length) {
            return (
              <Box
                key={`lobby-slots_${game.id}`}
                ref={ref}
                sx={{
                  gridColumnStart: "span 2",
                  gridRowStart: "span 2",
                  aspectRatio: "1 / 1",
                }}
              >
                <SlotCard
                  cardId={game.id}
                  src={game.image_url}
                  title={game.title}
                  top={game.popular}
                  recs={game.recommended}
                  new={game.new}
                  live={game.animals}
                  disabled={!game.available}
                  favorites={favorites}
                />
              </Box>
            );
          }
          return (
            <SlotCard
              key={`lobby-slots_${game.id}`}
              cardId={game.id}
              src={game.image_url}
              title={game.title}
              top={game.popular}
              recs={game.recommended}
              new={game.new}
              live={game.animals}
              disabled={!game.available}
              favorites={favorites}
            />
          );
        })}
        <LobbySlotsSkeletonSection perPage={perPage} isLoading={loading} />
      </Box>
    </Box>
  );
};

interface ILobbySlotsSectionFilteredByProviderProps {
  action:
    | "popular"
    | "recommended"
    | "bonus_buy"
    | "new"
    | "slots"
    | "live"
    | "crash_games"
    | "roulette"
    | "card_games"
    | "jackpots"
    | "mega_ways"
    | "animals"
    | "fruits"
    | "seven"
    | "search"
    | "books";
  provider?: number;
}
export const LobbySlotsSectionFilteredByProvider = ({
  action,
  provider,
}: ILobbySlotsSectionFilteredByProviderProps) => {
  const twoItems = useMediaQuery({ maxWidth: 374.98 });
  const threeItems = useMediaQuery({ minWidth: 374.98, maxWidth: 599.98 });
  const fourItems = useMediaQuery({ minWidth: 599.98, maxWidth: 1029.98 });
  const sixItems = useMediaQuery({ minWidth: 1029.98, maxWidth: 1699.98 });
  const eightItems = useMediaQuery({ minWidth: 1699.98 });
  const perPage = twoItems
    ? 50
    : threeItems
    ? 51
    : fourItems
    ? 52
    : sixItems
    ? 54
    : eightItems
    ? 56
    : 50;
  const [page, setPage] = useState<number>(1);
  const [object, setObject] = useState<{
    [key: string]: string | number | boolean | undefined;
    page: number;
    provider: number | undefined;
  }>({ ...slotsQueryObjectHelper(action, page, provider), perPage });
  const slotsData = useAppSelector(
    LobbySlotsProviderSelector(slotsAttributeHelper(object), provider)
  );
  const favorites = useAppSelector(({ slots }) => slots.favouriteGames);
  const provider_elem = slotsData.provider;
  const [games, setGames] = useState<IStoreSlot[]>([]);
  const gamesCount: number = slotsData ? slotsData.count : 0;
  const [loading, setLoading] = useState<boolean>(false);
  const { ref, inView } = useInView();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  async function fetchSlots() {
    setLoading(true);
    const slotsResponse = await dispatch(
      getTSlots({
        ...object,
        attribute: slotsAttributeHelper(object),
      })
    );
    if (slotsResponse.type === "gettSlots/fulfilled") {
      setLoading(false);
    }
  }
  useEffect(() => {
    if (provider) {
      setObject({
        ...slotsQueryObjectHelper(action, 1, provider),
        perPage,
      });
      setGames([]);
      setPage(1);
    }
  }, [provider]);
  useEffect(() => {
    setObject({
      ...slotsQueryObjectHelper(action, page, provider),
      perPage,
    });
  }, [page]);
  useEffect(() => {
    if (slotsData && slotsData.games.length > 0) {
      setGames((prev) => {
        const uniqueGames =
          page === 1 ? slotsData.games : [...prev, ...slotsData.games];
        return uniqueGames.filter(
          (game, index, self) =>
            self.findIndex((g) => g.id === game.id) === index
        );
      });
    } else if (!loading) {
      fetchSlots();
    }
  }, [slotsAttributeHelper(object), slotsData.games.length]);

  useEffect(() => {
    if (inView && !loading && Math.ceil(gamesCount / perPage) > page) {
      setPage((prev) => prev + 1);
    }
  }, [inView]);
  if (!provider_elem) {
    return (
      <Box className="lobby-slots-section">
        <Box className="lobby-slots-section-provider__error-heading">
          {t("slots.errors.heading.provider_not_found")}
        </Box>
      </Box>
    );
  }
  return (
    <Box className="lobby-slots-section">
      <Box className="lobby-slots-section-provider__heading">
        {provider_elem.name}
      </Box>
      <Box
        className={`lobby-slots-section__container ${
          twoItems ? "two-items" : ""
        }${threeItems ? "three-items" : ""}${fourItems ? "four-items" : ""}${
          sixItems ? "six-items" : ""
        }${eightItems ? "eight-items" : ""}`}
      >
        {games.map((game, index) => {
          if (index + 1 === games.length) {
            return (
              <Box
                key={`lobby-slots_${game.id}`}
                ref={ref}
                sx={{
                  gridColumnStart: "span 2",
                  gridRowStart: "span 2",
                  aspectRatio: "1 / 1",
                }}
              >
                <SlotCard
                  cardId={game.id}
                  src={game.image_url}
                  title={game.title}
                  top={game.popular}
                  recs={game.recommended}
                  new={game.new}
                  live={game.animals}
                  disabled={!game.available}
                  favorites={favorites}
                />
              </Box>
            );
          }
          return (
            <SlotCard
              key={`lobby-slots_${game.id}`}
              cardId={game.id}
              src={game.image_url}
              title={game.title}
              top={game.popular}
              recs={game.recommended}
              new={game.new}
              live={game.animals}
              disabled={!game.available}
              favorites={favorites}
            />
          );
        })}
        <LobbySlotsSkeletonSection perPage={perPage} isLoading={loading} />
      </Box>
    </Box>
  );
};
